import {
  CSS_SCOPE_ATTRIBUTE,
  cssScopeToken,
  scopeCustomCss,
} from "@redotech/order-tracking-common/scope-custom-css";
import { pageMinHeightCss } from "@redotech/order-tracking-common/tracking-page-definition";
import {
  BrandKit,
  defaultBrandKit,
} from "@redotech/redo-content-builder-common/brand-kit";
import { getFontFamilyWithFallback } from "@redotech/redo-content-builder-common/brand-kit/font-utils";
import { OrderLookupSettings } from "@redotech/redo-order-tracking-lookup-common/order-lookup-defaults";
import {
  memo,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { BrandKitButton } from "../brand-kit-button";
import { BrandKitTextInput } from "../brand-kit-text-input";
import { TeamContext } from "../context/team-context";
import { Flex } from "../flex";
import { Text } from "../text";
import * as styles from "./embedded-order-tracking-login.module.css";

/** Merchant-facing CSS hooks. The spec asserts every one of these renders. */
export const ORDER_LOOKUP_CSS_TARGETS = [
  "order-lookup-background",
  "order-lookup-card",
  "order-lookup-title",
  "order-lookup-required-note",
  "order-lookup-field-email",
  "order-lookup-field-order",
  "order-lookup-label",
  "order-lookup-input",
  "order-lookup-button",
  "order-lookup-error",
] as const;

export const EmbeddedOrderTrackingLogin = memo(
  function EmbeddedOrderTrackingLogin({
    settings,
    brandKit: brandKitProp,
    onSubmit,
    pending,
    error,
    scopeCustomCssToCard = false,
  }: {
    settings: OrderLookupSettings;
    /** Pass explicitly for storefront use (no TeamContext available). */
    brandKit: BrandKit | undefined;
    onSubmit:
      | ((emailOrZip: string, orderIdTrackingNumber: string) => void)
      | undefined;
    pending: boolean | undefined;
    error: string | undefined;
    /**
     * Set on merchant-app surfaces, which render outside the storefront's
     * shadow root and would otherwise let merchant CSS restyle the app.
     */
    scopeCustomCssToCard?: boolean;
  }) {
    const team = useContext(TeamContext);
    const brandKit =
      brandKitProp ?? team?.settings?.brandKit ?? defaultBrandKit;
    const [emailZip, setEmailZip] = useState("");
    const [orderTrackingNumber, setOrderTrackingNumber] = useState("");
    const errorRef = useRef<HTMLDivElement>(null);
    const errorId = useId();
    const emailInputId = useId();
    const orderInputId = useId();
    const scopeToken = cssScopeToken(useId());
    const prevErrorRef = useRef<string | undefined>();

    const emailLabel = settings.emailLabel ?? "Email or zip code";
    const emailPlaceholder =
      settings.emailPlaceholder ?? "Enter email or zip...";
    const orderLabel = settings.orderLabel ?? "Order # or tracking #";
    const orderPlaceholder =
      settings.orderPlaceholder ?? "Enter order or tracking #...";

    const titleFontFamily = getFontFamilyWithFallback(
      settings.titleFontFamily ?? brandKit.font.headerFontFamily,
      brandKit.customFontFamilies,
    );
    const bodyFontFamily = getFontFamilyWithFallback(
      settings.bodyFontFamily ?? brandKit.font.bodyFontFamily,
      brandKit.customFontFamilies,
    );

    // Override brand kit fonts with order lookup settings so that
    // BrandKitButton and BrandKitTextInput use the configured fonts
    const effectiveBrandKit = useMemo(
      (): BrandKit => ({
        ...brandKit,
        font: {
          ...brandKit.font,
          bodyFontFamily:
            settings.bodyFontFamily ?? brandKit.font.bodyFontFamily,
          headerFontFamily:
            settings.titleFontFamily ?? brandKit.font.headerFontFamily,
        },
      }),
      [brandKit, settings.bodyFontFamily, settings.titleFontFamily],
    );

    const submitDisabled =
      onSubmit !== undefined && (!emailZip || !orderTrackingNumber || pending);

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (!submitDisabled && onSubmit) {
        onSubmit(emailZip, orderTrackingNumber);
      }
    };

    useEffect(
      function autofillFromParams() {
        const email = decodeURIComponent(
          new URLSearchParams(window.location.search).get("rtp_email") ?? "",
        );
        const trackingNumber = decodeURIComponent(
          new URLSearchParams(window.location.search).get(
            "rtp_tracking_number",
          ) ?? "",
        );
        if (
          email &&
          trackingNumber &&
          email !== emailZip &&
          trackingNumber !== orderTrackingNumber &&
          onSubmit
        ) {
          setEmailZip(email);
          setOrderTrackingNumber(trackingNumber);
          void onSubmit(email, trackingNumber);
        }
      },
      [
        onSubmit,
        setEmailZip,
        setOrderTrackingNumber,
        emailZip,
        orderTrackingNumber,
      ],
    );

    useEffect(
      function focusErrorOnAppear() {
        if (error && !prevErrorRef.current) {
          errorRef.current?.focus();
        }
        prevErrorRef.current = error;
      },
      [error],
    );

    const inputOverride = settings.inputOverride?.enabled
      ? {
          ...settings.inputOverride,
          font: {
            ...settings.inputOverride.font,
            fontFamily:
              settings.bodyFontFamily ?? settings.inputOverride.font.fontFamily,
          },
        }
      : undefined;

    const rawCustomCss = settings.customCss ?? "";
    const customCss = scopeCustomCssToCard
      ? scopeCustomCss(rawCustomCss, scopeToken)
      : rawCustomCss;

    return (
      <div
        className={styles.background}
        data-target="order-lookup-background"
        style={{
          backgroundColor: settings.backgroundColor,
          backgroundImage: settings.backgroundImageUrl
            ? `url(${settings.backgroundImageUrl})`
            : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: settings.minPageHeightPx
            ? pageMinHeightCss(settings.minPageHeightPx, "100%")
            : undefined,
        }}
        {...{ [CSS_SCOPE_ATTRIBUTE]: scopeToken }}
      >
        {customCss ? <style>{customCss}</style> : null}
        <form
          className={styles.card}
          data-target="order-lookup-card"
          onSubmit={handleSubmit}
          style={{
            backgroundColor: settings.cardBackgroundColor,
            borderRadius: settings.cardBorderRadius,
            borderWidth: settings.cardBorderWidth,
            borderStyle: "solid",
            borderColor: settings.cardBorderColor,
            boxShadow: settings.cardShadowEnabled ? undefined : "none",
          }}
        >
          {settings.titleText && (
            <Text
              as="h1"
              dataTarget="order-lookup-title"
              fontSize="xl"
              fontWeight="semibold"
              style={{
                color: settings.titleColor,
                fontFamily: titleFontFamily,
                transition: "none",
              }}
            >
              {settings.titleText}
            </Text>
          )}
          <Flex dir="column" gap="lg">
            <p
              data-target="order-lookup-required-note"
              style={{
                margin: "var(--redo-spacing-none)",
                fontSize: "var(--redo-font-size-xs)",
                color: settings.titleColor,
                fontFamily: bodyFontFamily,
                opacity: 0.75,
              }}
            >
              * Both fields are required
            </p>
            <Flex data-target="order-lookup-field-email" dir="column" gap="xs">
              <label
                className={styles.label}
                data-target="order-lookup-label"
                htmlFor={emailInputId}
                style={{
                  color: settings.titleColor,
                  fontFamily: bodyFontFamily,
                }}
              >
                {emailLabel}
              </label>
              <BrandKitTextInput
                ariaDescribedBy={error ? errorId : undefined}
                ariaInvalid={!!error}
                brandKit={effectiveBrandKit}
                dataTarget="order-lookup-input"
                htmlId={emailInputId}
                override={inputOverride}
                placeholder={emailPlaceholder}
                required
                setValue={setEmailZip}
                value={emailZip}
              />
            </Flex>

            <Flex data-target="order-lookup-field-order" dir="column" gap="xs">
              <label
                className={styles.label}
                data-target="order-lookup-label"
                htmlFor={orderInputId}
                style={{
                  color: settings.titleColor,
                  fontFamily: bodyFontFamily,
                }}
              >
                {orderLabel}
              </label>
              <BrandKitTextInput
                ariaDescribedBy={error ? errorId : undefined}
                ariaInvalid={!!error}
                brandKit={effectiveBrandKit}
                dataTarget="order-lookup-input"
                htmlId={orderInputId}
                override={inputOverride}
                placeholder={orderPlaceholder}
                required
                setValue={setOrderTrackingNumber}
                value={orderTrackingNumber}
              />
            </Flex>
          </Flex>
          <BrandKitButton
            brandKit={effectiveBrandKit}
            className={styles.button}
            dataTarget="order-lookup-button"
            disabled={submitDisabled}
            globalOverride={
              settings.buttonOverride?.enabled
                ? {
                    primary: {
                      ...settings.buttonOverride,
                      font: {
                        ...settings.buttonOverride.font,
                        fontFamily:
                          settings.bodyFontFamily ??
                          settings.buttonOverride.font.fontFamily,
                      },
                    },
                    secondary: settings.buttonOverride,
                    tertiary: settings.buttonOverride,
                  }
                : undefined
            }
            hierarchy="primary"
            pending={pending}
            text={settings.buttonText}
            type="submit"
          />
          {error && (
            <div
              data-target="order-lookup-error"
              id={errorId}
              ref={errorRef}
              role="alert"
              style={{ outline: "none" }}
              tabIndex={-1}
            >
              <Text fontSize="sm" textColor="error">
                {error}
              </Text>
            </div>
          )}
        </form>
      </div>
    );
  },
);
